// i18n
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Avatar from 'presentations/views/components/atoms/Avatar';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  fontSize,
  fontWeight,
  mediaMax,
  mediaMin,
  spacing,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import { hostEventTypeEditPath } from 'presentations/routes/path';
import { black, gray05 } from 'presentations/views/styles/color';
import { ServiceDetailListEventTypeItem } from 'presentations/api/schema/event_type';
import { splitHostAndUsersFromEventType } from 'presentations/views/helpers/event_type';
import { transformImageUrl } from 'presentations/assets';
import { ViewerItem } from 'presentations/api/schema/user';
import { Role as TeamUserRole } from 'domains/models/team_user/role';
import { Role as EventTypeUserRole } from 'domains/models/event_type_user/role';

interface Props {
  viewer?: ViewerItem;
  eventTypes: ServiceDetailListEventTypeItem[];
  onClose: () => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  return (
    <Dialog isOpen onClose={props.onClose} title={t('title')} disableMinWidth>
      <Container>
        {props.eventTypes.map((eventType) => {
          const { host, users } = splitHostAndUsersFromEventType(
            eventType.users,
          );
          let isEditable =
            props.viewer && props.viewer.role <= TeamUserRole.Admin;
          if (!isEditable && props.viewer) {
            isEditable = eventType.users.some((user) => {
              return (
                user.uid === props.viewer?.uid &&
                user.role <= EventTypeUserRole.Editor
              );
            });
          }
          return (
            <EventTypeContainer>
              <EventTypeInfo>
                <EventTypeLabel>{eventType.name}</EventTypeLabel>
                <UserList>
                  {host && (
                    <>
                      <Invitor>
                        <User
                          src={transformImageUrl(host.profileImage, {
                            width: 32,
                            height: 32,
                          })}
                          alt={host.name || undefined}
                        />
                      </Invitor>
                      <UserName>
                        {host.name}{' '}
                        <Others>
                          {users.length > 0 &&
                            t('Member', { num: users.length })}
                        </Others>
                      </UserName>
                    </>
                  )}
                </UserList>
              </EventTypeInfo>

              {isEditable && (
                <EventTypeLink
                  href={hostEventTypeEditPath(eventType.uid)}
                  key={eventType.uid}
                >
                  <EditIcon name="edit" color={'primary'} />
                  <EditTitle>{t('Edit')}</EditTitle>
                </EventTypeLink>
              )}
            </EventTypeContainer>
          );
        })}
      </Container>
    </Dialog>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const EventTypeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${spacing(16)} ${spacing(8)};
  border-top: 1px solid ${gray05};
  :last-of-type {
    border-bottom: 1px solid ${gray05};
  }

  width: 540px;
  ${mediaMax.tablet} {
    width: 295px;
  }
`;
const EventTypeInfo = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaMax.tablet} {
    max-width: 185px;
  }
`;
const EventTypeLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${black};
`;
const EventTypeLabel = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;
const EditIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
`;
const EditTitle = styled.span`
  margin-left: ${spacing(6)};
  min-width: 40px;
`;

const User = styled(Avatar)`
  width: 32px;
  height: 32px;
`;
const UserList = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(8)};
`;
const Invitor = styled.div`
  margin-right: ${spacing(8)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: start;
  }
`;
const UserName = styled.span`
  color: ${black};
  font-weight: ${fontWeight('bold')};

  font-size: ${fontSize(12)};
`;
const Others = styled.span`
  ${mediaMax.tablet} {
    display: none;
  }
`;
